/** Mensaje legible para cualquier valor lanzado o capturado en `catch`. */
export function getErrorMessage(error: unknown): string {
  if (error instanceof Error) return error.message
  if (typeof error === 'string') return error
  if (error && typeof error === 'object' && 'message' in error) {
    const m = (error as { message?: unknown }).message
    if (typeof m === 'string') return m
  }
  try {
    return JSON.stringify(error)
  } catch {
    return 'Unknown error'
  }
}

/** Cuerpo `_data` típico de errores Nitro/ofetch (`response._data`). */
function readFetchErrorData(error: unknown): ApiErrorPayload | string | undefined {
  if (!error || typeof error !== 'object') return undefined
  if (!('data' in error)) return undefined
  const data = (error as { data?: unknown }).data
  if (typeof data === 'string') return data
  if (data && typeof data === 'object') return data as ApiErrorPayload
  return undefined
}

/**
 * Mensaje para errores de `$fetch` / ofetch en cliente (prioriza `data.message`, `details[0].message`).
 */
export function getFetchErrorMessage(error: unknown): string {
  const data = readFetchErrorData(error)
  if (typeof data === 'string') return data
  if (data?.message) return String(data.message)
  if (data?.msg) return String(data.msg)
  const d0 = data?.details?.[0]
  if (d0 && typeof d0 === 'object' && 'message' in d0 && typeof (d0 as { message?: unknown }).message === 'string') {
    return String((d0 as { message: string }).message)
  }
  return getErrorMessage(error)
}

/**
 * Variante usada cuando el error expone `data` como en algunos handlers (p. ej. post-checkout).
 */
export function getClientHttpErrorMessage(error: unknown): string {
  if (!error || typeof error !== 'object') return getErrorMessage(error)
  const e = error as { data?: { message?: string; statusMessage?: string }; message?: string }
  return e.data?.message || e.data?.statusMessage || e.message || getErrorMessage(error)
}

/** Datos anidados `response._data` en errores tipo H3/fetch del servidor. */
export function getNestedResponseData(error: unknown): unknown {
  if (!error || typeof error !== 'object' || !('response' in error)) return undefined
  const r = (error as { response?: { _data?: unknown } }).response
  return r?._data
}

/** `statusCode` en errores H3 / ofetch (`$fetch` en servidor). */
export function getThrownStatusCode(error: unknown): number | undefined {
  if (!error || typeof error !== 'object' || !('statusCode' in error)) return undefined
  const c = (error as { statusCode?: unknown }).statusCode
  return typeof c === 'number' ? c : undefined
}
